import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import '../styles/booking.css';

export const CustomSelect = ({ options, value, onChange, placeholder, icon, error, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  const handleSelect = (optionValue, optionLabel) => {
    if (disabled) return;
    onChange(optionValue);
    setSelectedLabel(optionLabel);
    closeDropdown();
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) {
      closeDropdown();
    } else {
      setIsOpen(true);
      setIsClosing(false);
    }
  };

  const closeDropdown = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    closeDropdown();
  };

  React.useEffect(() => {
    if (value) {
      const selectedOption = options.find(option => option.value === value);
      if (selectedOption) {
        setSelectedLabel(selectedOption.label);
      }
    } else {
      setSelectedLabel('');
    }
  }, [value, options]);

  return (
    <div className="custom-select">
      <div 
        className={`select-input ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleToggle}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <div className="select-value" style={{ opacity: disabled ? 0.5 : 1 }}>
          {selectedLabel || placeholder}
        </div>
        {icon && <div className="select-icon" style={{ opacity: disabled ? 0.5 : 1 }}>{icon}</div>}
      </div>
      
      {isOpen && !disabled && (
        <div className={`select-dropdown ${isClosing ? 'closing' : ''}`}>
          <div className="select-options">
            {options.map((option) => (
              <div 
                key={option.value}
                className={`select-option ${value === option.value ? 'selected' : ''}`}
                onClick={() => handleSelect(option.value, option.label)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {isOpen && !disabled && (
        <div className="select-backdrop" onClick={handleClose}></div>
      )}
    </div>
  );
};

export const CustomDatePicker = ({ selectedDate, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isClosing, setIsClosing] = useState(false);
  const [slideDirection, setSlideDirection] = useState('');
  const [isSliding, setIsSliding] = useState(false);

  const months = [
    'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
    'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'
  ];

  const weekDays = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      // Створюємо дату в полудень щоб уникнути проблем з часовими поясами
      days.push(new Date(year, month, day, 12, 0, 0));
    }
    
    return days;
  };

  const days = getDaysInMonth(currentMonth);

  const handlePrevMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSlideDirection('right');
    setIsSliding(true);
    setTimeout(() => {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
      setIsSliding(false);
    }, 150);
  };

  const handleNextMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSlideDirection('left');
    setIsSliding(true);
    setTimeout(() => {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
      setIsSliding(false);
    }, 150);
  };

  const handleDateSelect = (date) => {
    onChange(date);
    closeCalendar();
  };

  const handleToggle = () => {
    if (isOpen) {
      closeCalendar();
    } else {
      setIsOpen(true);
      setIsClosing(false);
    }
  };

  const closeCalendar = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    closeCalendar();
  };

  const isToday = (date) => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    return date && 
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    return date && selectedDate &&
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear();
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const dateToCheck = new Date(date);
    dateToCheck.setHours(12, 0, 0, 0);
    return dateToCheck < today;
  };

  return (
    <div className="custom-date-picker">
      <div 
        className={`date-input ${error ? 'error' : ''}`}
        onClick={handleToggle}
      >
        {selectedDate ? selectedDate.toLocaleDateString('uk-UA') : 'Оберіть дату'}
        <Calendar size={18} className="calendar-icon" />
      </div>
      
      {isOpen && (
        <div className={`calendar-dropdown ${isClosing ? 'closing' : ''}`}>
          <div className="calendar-header">
            <button onClick={handlePrevMonth} className="nav-button" type="button">‹</button>
            <div className="month-year">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button onClick={handleNextMonth} className="nav-button" type="button">›</button>
          </div>
          
          <div className={`calendar-grid ${isSliding ? `sliding slide-${slideDirection}` : 'slide-in'}`}>
            {weekDays.map(day => (
              <div key={day} className="week-day">{day}</div>
            ))}
            
            {days.map((date, index) => (
              <div 
                key={index}
                className={`calendar-day ${
                  !date ? 'empty' : 
                  isPastDate(date) ? 'past' : 
                  isSelected(date) ? 'selected' : 
                  isToday(date) ? 'today' : ''
                }`}
                onClick={() => date && !isPastDate(date) && handleDateSelect(date)}
              >
                {date ? date.getDate() : ''}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {isOpen && (
        <div className="calendar-backdrop" onClick={handleClose}></div>
      )}
    </div>
  );
};
